import * as React from 'react';
import { cn } from '../lib/cn';

export interface UniversalLoadingStateProps {
  /** Layout: 'rows' for table-like skeletons, 'cards' for grid, 'spinner' for centered indeterminate. */
  variant?: 'rows' | 'cards' | 'spinner';
  /** Number of skeleton rows / cards to render. Defaults to 5. */
  count?: number;
  /** Optional caption shown below the spinner variant. */
  caption?: string;
  className?: string;
}

/**
 * UniversalLoadingState — consistent loading UI for tables, lists, and full pages.
 */
export function UniversalLoadingState({
  variant = 'rows',
  count = 5,
  caption,
  className,
}: UniversalLoadingStateProps) {
  if (variant === 'spinner') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3 py-8', className)}>
        <Spinner />
        {caption && <p className="text-sm text-muted-foreground">{caption}</p>}
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="space-y-3 rounded border border-border bg-card p-4">
            <SkeletonLine className="h-4 w-2/3" />
            <SkeletonLine className="h-3 w-full" />
            <SkeletonLine className="h-3 w-5/6" />
          </div>
        ))}
      </div>
    );
  }

  // rows
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <SkeletonLine className="h-4 w-1/4" />
          <SkeletonLine className="h-4 w-1/3" />
          <SkeletonLine className="h-4 w-1/6" />
          <SkeletonLine className="ml-auto h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

function SkeletonLine({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-sm bg-muted',
        className
      )}
      aria-hidden="true"
    />
  );
}

function Spinner() {
  return (
    <svg
      className="h-6 w-6 animate-spin text-primary"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}
